import { MeasurementEnvironmentError } from './MeasurementEnvironmentError';

export interface MeasurementHostOptions {
  expectedDevicePixelRatio?: number;
  customFonts?: Array<{ family: string; source: string; descriptors?: FontFaceDescriptors } | string>;
}

export interface MeasurementHostApi {
  start(): Promise<void>;
  getContainer(): HTMLElement;
  teardown(): Promise<void>;
  fontsReady(): Promise<void>;
}

let container: HTMLElement | null = null;

function ensureContainer(): HTMLElement {
  if (container) return container;
  const host = document.createElement('div');
  host.setAttribute('data-edgedocs-measurement-host', '');
  // CSS reset for deterministic isolation
  (host.style as any).all = 'initial';
  host.style.boxSizing = 'border-box';
  host.style.margin = '0';
  host.style.padding = '0';
  host.style.fontSize = '12pt';
  host.style.lineHeight = '1.2';
  host.style.letterSpacing = 'normal';
  host.style.fontFamily = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  host.style.fontWeight = 'normal';
  host.style.fontStyle = 'normal';
  // Ensure crisp text measurement
  (host.style as any).webkitFontSmoothing = 'antialiased';
  (host.style as any).mozOsxFontSmoothing = 'grayscale';
  // Off-screen positioning
  host.style.position = 'fixed';
  host.style.left = '-10000px';
  host.style.top = '0px';
  host.style.width = '800px';
  host.style.height = '0px';
  host.style.overflow = 'hidden';
  host.style.visibility = 'hidden';
  document.body.appendChild(host);
  container = host;
  return host;
}

export function createMeasurementHost(opts: MeasurementHostOptions = {}): MeasurementHostApi {
  const expectedDpr = opts.expectedDevicePixelRatio;
  const customFonts = opts.customFonts || [];

  async function start(): Promise<void> {
    ensureContainer();
    if (expectedDpr && typeof window !== 'undefined') {
      const dpr = window.devicePixelRatio || 1;
      if (Math.abs(dpr - expectedDpr) > 0.01) {
        throw new MeasurementEnvironmentError(
          'UNSUPPORTED_DPR',
          'Device pixel ratio does not match expected value.',
          { actualDevicePixelRatio: dpr, expectedDevicePixelRatio: expectedDpr }
        );
      }
    }
    if (customFonts.length) {
      await loadCustomFonts(customFonts);
    }
    await fontsReady();
  }

  async function fontsReady(): Promise<void> {
    const anyDoc: any = document as any;
    if (anyDoc?.fonts?.ready && typeof anyDoc.fonts.ready.then === 'function') {
      await anyDoc.fonts.ready;
    }
  }

  async function loadCustomFonts(fonts: Array<{ family: string; source: string; descriptors?: FontFaceDescriptors } | string>): Promise<void> {
    const fontFaces: FontFace[] = [];
    const fontSet: any = (document as any).fonts;
    
    // Always ensure Inter font is loaded first
    try {
      await fontSet.load('16px Inter');
      await fontSet.load('400 16px Inter');
      await fontSet.load('700 16px Inter');
    } catch {
      console.warn('[MeasurementHost] Inter font not available, using system fallback');
    }
    
    for (const item of fonts) {
      if (typeof item === 'string') {
        // Expect CSS font shorthand: e.g., '12px "Inter"'
        try { await fontSet.load(item); } catch { /* ignore */ }
        continue;
      }
      try {
        const ff = new FontFace(item.family, `url(${item.source})`, item.descriptors || {});
        await ff.load();
        fontSet.add(ff);
        fontFaces.push(ff);
      } catch {
         
        console.warn(`[MeasurementHost] Failed to load custom font: ${item.family}`);
      }
    }
  }

  function getContainer(): HTMLElement {
    return ensureContainer();
  }

  async function teardown(): Promise<void> {
    if (container) {
      container.remove();
      container = null;
    }
  }

  return { start, getContainer, teardown, fontsReady };
}


